import React, { useEffect, useRef, useCallback } from 'react';
import './ChemicalFormulas.css';

/**
 * ChemicalFormulas — CSS-only animated background
 * Animations run on compositor thread (GPU) — never blocks scroll
 * 
 * Easter egg: When the center hub logo aligns with viewport center,
 * formula bubbles glow green (synced state)
 */

const FORMULAS = [
  // Hedera Protocol
  { formula: "H₃BAR", category: "protocol" },
  { formula: "DLT₂O₃", category: "protocol" },
  { formula: "C₆H₁₂NFT", category: "protocol" },
  { formula: "HG₄NET", category: "protocol" },
  { formula: "SC₂H₅OH", category: "protocol" },
  { formula: "HTS₂O", category: "protocol" },
  { formula: "CONS₄US", category: "protocol" },
  { formula: "NET₂₀₂₄", category: "protocol" },
  
  // WhataLab Ecosystem
  { formula: "WL₄B₆", category: "whatalab" },
  { formula: "WS₃NIP₄", category: "whatalab" },
  { formula: "WS₃CAN₅", category: "whatalab" },
  { formula: "WT₄RAD₃", category: "whatalab" },
  { formula: "WC₃LAI₆", category: "whatalab" },
  { formula: "WP₂AN₄", category: "whatalab" },
  { formula: "WC₃HEK₅", category: "whatalab" },
  { formula: "WB₂OST₄", category: "whatalab" },
  { formula: "WB₃RIG₄", category: "whatalab" },
  { formula: "WK₄IT₃", category: "whatalab" },
  
  // HTS Tokens
  { formula: "SC₃H₄", category: "token" },
  { formula: "K₂BL₅", category: "token" },
  { formula: "GiB₇H₂", category: "token" },
  { formula: "S₃NTX₄", category: "token" },
  { formula: "P₄CK₆", category: "token" },
  { formula: "HBX₃O₂", category: "token" },
  
  // NFT Collections
  { formula: "DP₂GC₃", category: "nft" },
  { formula: "WT₆IGR", category: "nft" },
  { formula: "Si₄WAS₃", category: "nft" },
  { formula: "W₄OKZ₅", category: "nft" },
  { formula: "B₃AHC₄", category: "nft" },
  { formula: "SL₃YNX₂", category: "nft" },
  { formula: "SH₄DR₃", category: "nft" },
  { formula: "E₄RTH₃", category: "nft" },
  
  // Crypto Slang
  { formula: "LF₅G₄", category: "slang" },
  { formula: "BUL₃H₂", category: "slang" },
  { formula: "BE₄AR₃", category: "slang" },
  { formula: "P₃ROF₄", category: "slang" },
  { formula: "MO₄ON₆", category: "slang" },
  { formula: "FO₃MO₂", category: "slang" },
  { formula: "HO₂DL₃", category: "slang" },
  { formula: "RE₄KT₅", category: "slang" }
];

// Pre-computed positions distributed across the viewport
const POSITIONS = [
  { x: 5, y: 8 },   { x: 85, y: 12 },  { x: 45, y: 5 },   { x: 25, y: 18 },
  { x: 70, y: 25 }, { x: 12, y: 35 },  { x: 55, y: 30 },  { x: 92, y: 40 },
  { x: 35, y: 45 }, { x: 78, y: 55 },  { x: 8, y: 60 },   { x: 62, y: 65 },
  { x: 20, y: 72 }, { x: 88, y: 78 },  { x: 42, y: 82 },  { x: 15, y: 88 },
  { x: 72, y: 15 }, { x: 30, y: 28 },  { x: 95, y: 58 },  { x: 50, y: 70 },
  { x: 3, y: 22 },  { x: 65, y: 42 },  { x: 38, y: 55 },  { x: 82, y: 68 },
  { x: 18, y: 48 }, { x: 58, y: 18 },  { x: 90, y: 85 },  { x: 28, y: 92 },
  { x: 75, y: 35 }, { x: 10, y: 75 },  { x: 48, y: 48 },  { x: 68, y: 88 },
  { x: 22, y: 62 }, { x: 85, y: 22 },  { x: 40, y: 38 },  { x: 55, y: 85 },
  { x: 8, y: 42 },  { x: 78, y: 72 },  { x: 32, y: 15 },  { x: 62, y: 52 },
  { x: 92, y: 32 }, { x: 15, y: 58 },  { x: 52, y: 22 },  { x: 88, y: 62 }
];

const ChemicalFormulas = () => {
  const syncTimeoutRef = useRef(null);
  
  // Check if the center hub is aligned with viewport center
  const checkSync = useCallback(() => {
    const hub = document.querySelector('.bf-hub');
    if (!hub) return;
    
    const rect = hub.getBoundingClientRect();
    const hubCenterY = rect.top + rect.height / 2;
    const viewportCenterY = window.innerHeight / 2;
    
    // Tolerance: within 80px of center (sweet spot for the effect)
    const tolerance = 80;
    const isAligned = Math.abs(hubCenterY - viewportCenterY) < tolerance;
    
    if (isAligned) {
      document.body.classList.add('formulas-synced');
    } else {
      document.body.classList.remove('formulas-synced');
    }
  }, []);
  
  // Scroll handler with debounce for performance
  const handleScroll = useCallback(() => {
    // Clear any existing timeout
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    
    // Check sync immediately on scroll
    checkSync();
    
    // Also check after scroll settles (for smooth scroll)
    syncTimeoutRef.current = setTimeout(checkSync, 50);
  }, [checkSync]);
  
  useEffect(() => {
    // Initial check
    checkSync();
    
    // Add scroll listener (passive for performance)
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', checkSync, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkSync);
      document.body.classList.remove('formulas-synced');
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    };
  }, [handleScroll, checkSync]);
  
  return (
    <div className="chemical-formulas-container">
      {FORMULAS.map((item, index) => {
        const pos = POSITIONS[index % POSITIONS.length];
        // Cycle through 8 animation variants
        const animVariant = (index % 8) + 1;
        // Stagger delays: 0s to 14s
        const delay = (index * 0.35) % 15;
        
        return (
          <div
            key={index}
            className={`chemical-formula chemical-formula--${item.category} cf-anim-${animVariant}`}
            style={{
              left: `${pos.x}%`,
              top: `${pos.y}%`,
              animationDelay: `${delay}s`
            }}
            title={`${item.formula} - ${item.category}`}
          >
            {item.formula}
          </div>
        );
      })}
    </div>
  );
};

export default ChemicalFormulas;
